/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useRef, useState} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {getCSSVariableValue} from '../../../../../_Montdor/assets/ts/_utils'
import {Link} from 'react-router-dom'
import {IAdminDashboardModel} from '../../../../models/dashboard-page/IDashboardModel'
import {getAdminCountApi} from '../../../../modules/dashboard-page/DashboardCRUD'
import {toast} from 'react-toastify'

type Props = {
  className: string
  chartColor: string
  strokeColor: string
  chartHeight: string
}

interface IState {
  loading: boolean
  list: IAdminDashboardModel
}

// ── Stat tile config ─────────────────────────────────────────────────────────
const STAT_TILES = [
  {
    key: 'cntQuotTotal',
    label: 'Total Quotation',
    icon: '📋',
    bgClass: 'qd-tile-amber',
    textClass: 'qd-tile-amber-text',
  },
  {
    key: 'cntQuotDIY',
    label: 'DIY Quotation',
    icon: '🔧',
    bgClass: 'qd-tile-teal',
    textClass: 'qd-tile-teal-text',
  },
  {
    key: 'cntQuotPrem',
    label: 'Premium Quotation',
    icon: '⭐',
    bgClass: 'qd-tile-violet',
    textClass: 'qd-tile-violet-text',
  },
  {
    key: 'cntQuotStand',
    label: 'Standard Quotation',
    icon: '📄',
    bgClass: 'qd-tile-slate',
    textClass: 'qd-tile-slate-text',
  },
  {
    key: 'cntQuotEsst',
    label: 'Essential Quotation',
    icon: '💡',
    bgClass: 'qd-tile-rose',
    textClass: 'qd-tile-rose-text',
  },
  {
    key: 'cntProduct',
    label: 'Products',
    icon: '📦',
    bgClass: 'qd-tile-emerald',
    textClass: 'qd-tile-emerald-text',
  },
  {
    key: 'cntCategory',
    label: 'Categories',
    icon: '🗂️',
    bgClass: 'qd-tile-teal',
    textClass: 'qd-tile-teal-text',
  },
  {
    key: 'cntplanArea',
    label: 'Plan Areas',
    icon: '📐',
    bgClass: 'qd-tile-amber',
    textClass: 'qd-tile-amber-text',
  },
  {
    key: 'cntEmployee',
    label: 'Employees',
    icon: '👤',
    bgClass: 'qd-tile-violet',
    textClass: 'qd-tile-violet-text',
  },
  {
    key: 'cntCustomer',
    label: 'Customers',
    icon: '🤝',
    bgClass: 'qd-tile-emerald',
    textClass: 'qd-tile-emerald-text',
  },
  {
    key: 'cntUser',
    label: 'Users',
    icon: '👥',
    bgClass: 'qd-tile-rose',
    textClass: 'qd-tile-rose-text',
  },
  {
    key: 'cntBranch',
    label: 'Branches',
    icon: '🏢',
    bgClass: 'qd-tile-slate',
    textClass: 'qd-tile-slate-text',
  },
]

const AdminCount: React.FC<Props> = ({className, chartColor, chartHeight, strokeColor}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)

  let [state, setState] = useState<IState>({
    loading: false,
    list: {} as IAdminDashboardModel,
  })

  useEffect(() => {
    fetchAdminCountList()
  }, [])

  useEffect(() => {
    if (!chartRef.current || loading) return

    const chart = new ApexCharts(
      chartRef.current,
      chartOptions(chartHeight, chartColor, strokeColor, state.list)
    )

    chart.render()

    return () => {
      chart.destroy()
    }
  }, [state.list])

  function fetchAdminCountList() {
    getAdminCountApi()
      .then((response) => {
        const responseData = response.data
        if (responseData.isSuccess === true) {
          setState({...state, list: responseData, loading: false})
        } else {
          toast.error(`${responseData.message}`)
          setState({...state, list: {} as IAdminDashboardModel, loading: false})
        }
      })
      .catch((error) => {
        toast.error(`${error}`)
        setState({...state, list: {} as IAdminDashboardModel, loading: false})
      })
  }

  const {list, loading} = state

  return (
    <>
      {/* ── Inline tile styles (scoped, no build step needed) ── */}
      <style>{`
        .qd-admin-card { border-radius: 14px !important; overflow: hidden; }
        .qd-admin-header {
          background: linear-gradient(135deg, #0F172A 0%, #1E3A5F 100%);
          padding: 20px 24px 16px;
          border-bottom: none !important;
        }
        .qd-admin-header h3 { color: #F8FAFC !important; font-size: 16px !important; font-weight: 700 !important; margin: 0; }
        .qd-admin-header span { color: #94A3B8; font-size: 12px; }

        .qd-tile {
          border-radius: 10px;
          padding: 16px 18px;
          display: flex;
          align-items: flex-start;
          gap: 12px;
          transition: transform 0.2s, box-shadow 0.2s;
          cursor: default;
          min-height: 80px;
        }
        .qd-tile:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(15,23,42,0.1); }

        .qd-tile-icon {
          font-size: 20px;
          width: 38px;
          height: 38px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          background: rgba(255,255,255,0.6);
        }
        .qd-tile-content { flex: 1; }
        .qd-tile-value {
          font-size: 24px !important;
          font-weight: 800 !important;
          line-height: 1.1 !important;
          letter-spacing: -0.02em !important;
          display: block;
        }
        .qd-tile-label {
          font-size: 11px !important;
          font-weight: 600 !important;
          letter-spacing: 0.02em !important;
          opacity: 0.85;
          margin-top: 2px;
          display: block;
        }

        /* Tile color variants */
        .qd-tile-teal   { background: linear-gradient(135deg, #E0F2FE, #BAE6FD); border: 1px solid #BAE6FD; }
        .qd-tile-teal-text { color: #0284C7; }
        .qd-tile-emerald { background: linear-gradient(135deg, #ECFDF5, #A7F3D0); border: 1px solid #A7F3D0; }
        .qd-tile-emerald-text { color: #059669; }
        .qd-tile-violet { background: linear-gradient(135deg, #EDE9FE, #DDD6FE); border: 1px solid #DDD6FE; }
        .qd-tile-violet-text { color: #6D28D9; }
        .qd-tile-rose   { background: linear-gradient(135deg, #FFF1F2, #FECDD3); border: 1px solid #FECDD3; }
        .qd-tile-rose-text { color: #BE123C; }
        .qd-tile-amber  { background: linear-gradient(135deg, #FFFBEB, #FDE68A); border: 1px solid #FDE68A; }
        .qd-tile-amber-text { color: #B45309; }
        .qd-tile-slate  { background: linear-gradient(135deg, #F8FAFC, #E2E8F0); border: 1px solid #E2E8F0; }
        .qd-tile-slate-text { color: #334155; }

        /* Loading shimmer */
        .qd-shimmer {
          background: linear-gradient(90deg, #F1F5F9 25%, #E2E8F0 50%, #F1F5F9 75%);
          background-size: 200% 100%;
          animation: shimmer 1.4s infinite;
          border-radius: 6px;
          height: 24px;
          width: 60px;
        }
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      <div className={`card qd-admin-card ${className}`}>
        {/* ── Header ── */}
        <div className='qd-admin-header'>
          <h3>Aarambh Interior Statistics</h3>
          <span>Live dashboard overview</span>
        </div>

        {/* ── Chart area ── */}
        {/* {!loading && (
          <div
            ref={chartRef}
            className='mixed-widget-2-chart'
            style={{
              minHeight: '180px',
              background: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
            }}
          />
        )} */}

        {/* ── Stat tiles ── */}
        <div className='card-body'>
          <div className='row g-3'>
            {STAT_TILES.map((tile) => (
              <div key={tile.key} className='col-6 col-sm-6 col-md-3'>
                <div className={`qd-tile ${tile.bgClass}`}>
                  <div className='qd-tile-icon'>{tile.icon}</div>
                  <div className='qd-tile-content'>
                    {loading ? (
                      <div className='qd-shimmer' />
                    ) : (
                      <span className={`qd-tile-value ${tile.textClass}`}>
                        {(list as any)[tile.key] ?? '—'}
                      </span>
                    )}
                    <span className={`qd-tile-label ${tile.textClass}`}>{tile.label}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

const chartOptions = (
  chartHeight: string,
  chartColor: string,
  strokeColor: string,
  list: any
): ApexOptions => {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-gray-200')
  const color = getCSSVariableValue('--bs-' + chartColor)

  return {
    series: [
      {
        name: 'Statistics',
        data: [
          Number(list?.cntCustomer || 0),
          Number(list?.cntProduct || 0),
          Number(list?.cntEmployee || 0),
          Number(list?.cntUser || 0),
          Number(list?.cntBranch || 0),
          Number(list?.cntCategory || 0),
        ],
      },
    ],
    chart: {
      fontFamily: 'inherit',
      type: 'bar',
      height: 220,
      toolbar: {
        show: false,
      },
    },

    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: '45%',
      },
    },
    legend: {show: false},
    dataLabels: {enabled: false},
    fill: {type: 'gradient', gradient: {shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.0}},
    stroke: {
      curve: 'smooth',
      show: true,
      width: 3,
      colors: [strokeColor],
    },
    xaxis: {
      categories: ['Customers', 'Products', 'Employees', 'Users', 'Branches', 'Categories'],
    },
    yaxis: {
      labels: {
        show: true,
      },
    },
    tooltip: {
      style: {fontSize: '12px'},
      y: {formatter: (val) => `${val}`},
      marker: {show: false},
    },
    colors: [color],
    markers: {colors: [color], strokeColors: [strokeColor], strokeWidth: 3},
  }
}

export {AdminCount}
